import { AlarmOnOutlined, GroupOutlined, LinkOutlined, MoreVertRounded, Tonality } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem, Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { createColumnHelper, Row } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import {
  ActionItemDrawerContent,
  ActionItemStatusPicker,
  AvatarGroup,
  BorderlessTextField,
  ContextLabel,
  ItemDrawerTitle,
  Table,
  TableDrawerButton,
  useDrawer,
} from "src/components";
import { DEFAULT_ACTION_ITEM_STATUS, Links } from "src/constants";
import { ActionItemSummaryDto, useDeleteActionItem, useEditActionItem, UserDto } from "src/http";
import { useDestructiveConfirm } from "src/utilities";

const columnHelper = createColumnHelper<ActionItemSummaryDto>();

const columnsConfig = (
  handleActionItemBlurred: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    actionItem: ActionItemSummaryDto
  ) => void,
  handleShowDrawerClicked: (e: React.MouseEvent, actionItem: ActionItemSummaryDto) => void,
  handleActionsClicked: (e: React.MouseEvent<HTMLElement>, actionItem: ActionItemSummaryDto) => void,
  theme: Theme
) => {
  return [
    columnHelper.accessor("description", {
      id: "Name",
      enableHiding: false,
      size: 400,
      header: () => (
        <Box sx={{ textAlign: "left" }}>
          <Typography variant="caption">NAME</Typography>
        </Box>
      ),
      cell: (props) => (
        <Stack
          direction="row"
          sx={{
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
          }}
        >
          <BorderlessTextField
            value={props.row.original.description}
            onClick={(e) => props.row.original.userCanEdit && e.stopPropagation()}
            onBlur={(e) => handleActionItemBlurred(e, props.row.original)}
            placeholder="Enter item..."
            isEditable={props.row.original.userCanEdit}
            maxLength={512}
          />
          <TableDrawerButton onClick={(e) => handleShowDrawerClicked(e, props.row.original)} />
        </Stack>
      ),
    }),
    columnHelper.accessor("currentStatus", {
      id: "Status",
      size: 200,
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <Tonality color="disabled" />
          <Typography variant="caption">STATUS</Typography>
        </Stack>
      ),
      cell: (props) => (
        <ActionItemStatusPicker
          variant="select"
          actionItemGuid={props.row.original.guid}
          status={props.row.original.currentStatus ?? DEFAULT_ACTION_ITEM_STATUS}
          planGuid={props.row.original.teamPlanSection.planGuid}
          disabled={!props.row.original.userCanEdit}
        />
      ),
    }),
    columnHelper.accessor("owners", {
      id: "Ownership",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <GroupOutlined color="disabled" />
          <Typography variant="caption">OWNERSHIP</Typography>
        </Stack>
      ),
      cell: (props) => (
        <AvatarGroup
          max={5}
          users={props.row.original.owners}
          avatarProps={{
            size: "xsmall",
          }}
        />
      ),
    }),
    columnHelper.accessor("context", {
      id: "Context",
      size: 300,
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <LinkOutlined color={"disabled"} />
          <Typography variant="caption">CONTEXT</Typography>
        </Stack>
      ),
      cell: (props) => {
        if (!props.row.original.context) {
          return null;
        }

        return <ContextLabel context={props.row.original.context} hideTeamName />;
      },
    }),
    columnHelper.accessor("dueDate", {
      id: "Due Date",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <AlarmOnOutlined color={"disabled"} />
          <Typography variant="caption">DUE DATE</Typography>
        </Stack>
      ),
      cell: (props) => {
        const dueDate = props.row.original.dueDate != null ? DateTime.fromISO(props.row.original.dueDate) : null;

        if (dueDate == null) {
          return null;
        } else {
          return (
            <Typography sx={{ color: dueDate < DateTime.now() ? theme.palette.error.main : undefined }}>
              {dueDate.toLocaleString(DateTime.DATE_MED)}
            </Typography>
          );
        }
      },
    }),
    columnHelper.display({
      id: "Actions",
      size: 50,
      minSize: 50,
      enableResizing: false,
      enableHiding: false,
      meta: {
        hideColumnMenu: true,
        hideResizer: true,
      },
      cell: (props) => {
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mr: 0.25 }}>
            <IconButton size="small" onClick={(e) => handleActionsClicked(e, props.row.original)}>
              <MoreVertRounded />
            </IconButton>
          </Box>
        );
      },
    }),
  ];
};

interface Props {
  tableId: string;
  actionItems: ActionItemSummaryDto[];
  enableActions?: boolean;
  fetchActionItemSubitems: (actionItemGuid: string) => Promise<void>;
  onOwnershipChanged?: (actionItemGuid: string, actionItemDescription: string, removedOwners: UserDto[]) => void;
  sx?: SxProps;
}

const ActionItemsTable = (props: Props) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedItem, setSelectedItem] = useState<ActionItemSummaryDto>();
  const { mutate: editActionItem } = useEditActionItem();
  const { mutate: deleteActionItem } = useDeleteActionItem();
  const { showDrawer } = useDrawer();
  const theme = useTheme();
  const confirm = useDestructiveConfirm();

  const handleActionItemBlurred = useMemo(
    () => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, actionItem: ActionItemSummaryDto) => {
      if (actionItem.userCanEdit) {
        editActionItem({
          actionItemGuid: actionItem.guid,
          description: e.target.value,
          planGuid: actionItem.teamPlanSection.planGuid,
        });
      }
    },
    [editActionItem]
  );

  const handleShowDrawerClicked = useMemo(
    () => (e: React.MouseEvent, actionItem: ActionItemSummaryDto) => {
      e.stopPropagation();

      showDrawer({
        title: <ItemDrawerTitle link={Links.ActionItemDetail(actionItem.guid)} />,
        content: (
          <ActionItemDrawerContent actionItemGuid={actionItem.guid} onOwnershipChanged={props.onOwnershipChanged} />
        ),
      });
    },
    [props.onOwnershipChanged, showDrawer]
  );

  function handleActionsClicked(e: React.MouseEvent<HTMLElement>, actionItem: ActionItemSummaryDto) {
    setMenuAnchor(e.currentTarget);
    setSelectedItem(actionItem);
    e.stopPropagation();
  }

  async function handleDeleteClicked() {
    setMenuAnchor(null);

    if (!selectedItem) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Strategic Item",
      content: "Are you sure you want to delete this strategic item?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteActionItem({
      actionItemGuid: selectedItem.guid,
      planGuid: selectedItem.teamPlanSection.planGuid,
    });
  }

  async function handleExpandRowClicked(
    row: Row<ActionItemSummaryDto>,
    isLoadingSubRows: boolean,
    setIsLoadingSubRows: (isLoading: boolean) => void
  ) {
    const actionItem = row.original;

    if (isLoadingSubRows || !actionItem.hasSubitems) {
      return;
    }

    setIsLoadingSubRows(true);

    try {
      if (actionItem.subitems.length === 0) {
        await props.fetchActionItemSubitems(actionItem.guid);
      }

      row.getToggleExpandedHandler()();
    } finally {
      setIsLoadingSubRows(false);
    }
  }

  const columns = useMemo(
    () => columnsConfig(handleActionItemBlurred, handleShowDrawerClicked, handleActionsClicked, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleActionItemBlurred, handleShowDrawerClicked, theme, props.actionItems]
  );

  return (
    <>
      <Table
        tableId={props.tableId}
        data={props.actionItems}
        columns={columns}
        columnVisibility={{
          actions: props.enableActions ?? false,
        }}
        columnPinning={{
          right: ["Actions"],
        }}
        enableSorting={false}
        getRowCanExpand={(row) => row.original.hasSubitems}
        getSubrows={(row) => row.subitems ?? []}
        onExpandRowClicked={handleExpandRowClicked}
        sx={props.sx}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export { ActionItemsTable };
